import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { cn } from '@/lib/ui/utils';

export interface EventIdentityAsset {
  src: string;
  alt: string;
}

interface EventIdentityProps {
  eventLogo: EventIdentityAsset | null;
  participantLogos?: readonly EventIdentityAsset[] | null | undefined;
  size?: 'default' | 'event-time-card';
  className?: string | undefined;
}

const MARK_SIZE_CLASS = {
  default: 'size-10.5',
  'event-time-card': 'size-7.5 @min-[480px]/event-time:size-9.5',
} as const;

const MARK_RADIUS_CLASS = {
  default: 'rounded-item',
  'event-time-card':
    'rounded-[calc(var(--radius-item)+0.1875rem)] @min-[480px]/event-time:rounded-overlay-item',
} as const;

const EVENT_TIME_BULLET_CLASS = 'w-1.75 text-[11px]'; // ARBITRARY: approved UI 04 pair separator is 11px inside the 30px mobile identity.

function EventIdentityMark({
  asset,
  size,
}: {
  asset: EventIdentityAsset;
  size: NonNullable<EventIdentityProps['size']>;
}): ReactNode {
  const sizeClass = MARK_SIZE_CLASS[size];
  return (
    <span data-testid="event-identity-mark" className={cn('block shrink-0', sizeClass)}>
      {/* ALLOW-NSFW-FALSE: event identity marks are admin-curated official assets. */}
      <OptimizedImage
        variant="thumbnail"
        src={asset.src}
        alt={asset.alt}
        size={sizeClass}
        priority
        rounded={MARK_RADIUS_CLASS[size]}
        className="bg-transparent [--foreground-tertiary:transparent] [--image-halftone-surface:transparent]"
        imageClassName={cn(
          'select-none object-contain object-center drop-shadow-md',
          size === 'event-time-card' ? 'p-0.75' : 'p-1',
        )}
        loadReveal="none"
        nsfw={false}
        preview={false /* ALLOW-NO-PREVIEW: identity chrome is not lightbox media. */}
      />
    </span>
  );
}

export function EventIdentity({
  eventLogo,
  participantLogos,
  size = 'default',
  className,
}: EventIdentityProps): ReactNode {
  const hasParticipantPair = participantLogos?.length === 2;
  const marks = hasParticipantPair ? participantLogos : eventLogo ? [eventLogo] : [];

  if (marks.length === 0) return null;

  return (
    <div
      data-testid="event-identity"
      data-identity-kind={hasParticipantPair ? 'participants' : 'event'}
      className={cn(
        'relative flex shrink-0 items-center',
        hasParticipantPair &&
          (size === 'event-time-card' ? 'gap-2.5 @min-[480px]/event-time:gap-3.25' : 'gap-4.5'),
        className,
      )}
    >
      {marks.map((mark) => (
        <EventIdentityMark key={`${mark.src}:${mark.alt}`} asset={mark} size={size} />
      ))}
      {hasParticipantPair ? (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center leading-none text-foreground-tertiary',
            size === 'event-time-card' ? EVENT_TIME_BULLET_CLASS : 'w-2 text-caption',
          )}
        >
          •
        </span>
      ) : null}
    </div>
  );
}
